// UploadCenter.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box as MuiBox,
    Paper as MuiPaper,
    Typography as MuiTypography,
    Button as MuiButton,
    Alert as MuiAlert,
    Checkbox as MuiCheckbox,
    TextField as MuiTextField
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { UploadCloud } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";

import { T } from "@/components/FileViewer/filesV6";
import FileViewer, { FileViewerRef } from "@/components/FileViewer/FileViewer";
import FileUploadVectorFileModal from "@/components/Toolkit/FileUploadVectorFileModal";
import FileScopeModal, { SavedScopeData } from "@/components/FileViewer/FileScopeModal";
import { VectorFile } from "@/api/models/vector_file";
import { FileAssignments, queryFileAssignments } from "@/api/fileAssignments";
import { markWorkProductsNeedingRebuild } from "@/api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { useProgrammaticIntakeQueue } from "./useProgrammaticIntakeQueue";
import { NavigationBackButton } from "./NavigationBackButton";
import {
    getGoogleDrivePickerSession,
    getGoogleDriveStatus,
    importGoogleDriveRefs,
    initiateGoogleDriveOAuth,
    type GoogleDriveEntry
} from "@/api/integrations/googleDrive";
import { openOAuthPopup, waitForOAuthPopup } from "@/utils/oauthPopup";
import { openGoogleDrivePickerDialog } from "@/utils/googlePicker";

const DRIVE_OAUTH_RESULT_PARAM = "google_drive_oauth";

interface UploadCenterProps {
    hideHeader?: boolean;
    /** Files handed to this page programmatically (FDE chat drops →
     *  send_file_to_uploads). Fed one at a time through the same upload modal
     *  as a manual drop/pick. */
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    /** Fires after the user saves a scope assignment for an uploaded file —
     *  the host's hook point for the return-to-origin offer. */
    onAssignmentComplete?: () => void;
    /** Changes when an external action (such as FDE) updates file assignments. */
    refreshSignal?: number;
    /** Shown only when this page was opened from the product's Context Mesh. */
    onBackToContextMesh?: () => void;
    /** Product selected by default when uploads originate from a Product-linked Context Mesh. */
    defaultProjectId?: number;
    /** Standalone Context Mesh selected by default for uploads opened from its console. */
    defaultContextMeshId?: string;
}

export const UploadCenter: React.FC<UploadCenterProps> = ({
    hideHeader = false,
    intakeFiles,
    onIntakeConsumed,
    onAssignmentComplete,
    refreshSignal,
    onBackToContextMesh,
    defaultProjectId,
    defaultContextMeshId
}) => {
    const { getToken } = useAuth();
    const fileViewerRef = useRef<FileViewerRef>(null);

    const [fileOpen, setFileOpen] = useState(false);
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showScopeModal, setShowScopeModal] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<VectorFile | null>(null);
    const [uploadedFileAssignments, setUploadedFileAssignments] = useState<FileAssignments | null>(null);
    const [uploadedFileRequiresContextMesh, setUploadedFileRequiresContextMesh] = useState(false);
    const [pendingScopeAssignments, setPendingScopeAssignments] = useState<Array<{
        file: VectorFile;
        assignments: FileAssignments;
        requireContextMeshSelection: boolean;
    }>>([]);
    const [completedAssignments, setCompletedAssignments] = useState(0);
    const returnNotifiedForBatchRef = useRef(false);
    const [fileTypeError, setFileTypeError] = useState<string | null>(null);
    const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
    const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const [driveDialogOpen, setDriveDialogOpen] = useState(false);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveError, setDriveError] = useState<string | null>(null);
    const [driveEntries, setDriveEntries] = useState<GoogleDriveEntry[]>([]);
    const [driveSearch, setDriveSearch] = useState("");
    const [driveSelected, setDriveSelected] = useState<Record<string, GoogleDriveEntry>>({});

    // NEW: ref for a dedicated manual file input (button-driven)
    const manualInputRef = useRef<HTMLInputElement | null>(null);
    const lastRefreshSignalRef = useRef(refreshSignal);

    useEffect(() => {
        if (refreshSignal === undefined || refreshSignal === lastRefreshSignalRef.current) return;
        lastRefreshSignalRef.current = refreshSignal;
        fileViewerRef.current?.refresh();
    }, [refreshSignal]);

    // Fetch existing assignments after upload so re-uploads preserve Product
    // and standalone Context Mesh links.
    const fetchFileAssignments = useCallback(async (fileId: number): Promise<FileAssignments> => {
        const token = await getToken();
        if (!token) throw new Error("Authentication is required to load file assignments.");
        const assignments = await queryFileAssignments(token, [fileId]);
        const assignment = assignments[fileId];
        if (!assignment) throw new Error("The uploaded file's assignments could not be loaded.");
        return assignment;
    }, [getToken]);

    const queueAssignModalForFile = useCallback(async (
        fileId: number,
        fileName: string,
        requireContextMeshSelection = false
    ) => {
        const existingAssignments = await fetchFileAssignments(fileId);
        const queuedFile: VectorFile = {
            fileid: fileId,
            filename: fileName || `File #${fileId}`,
            userid: 0,
            mime_type: "",
            createdate: new Date(0),
            lastupdated: new Date(0),
            faq: "",
            overview: "",
            total_tokens: 0,
            search_index_chungs: 0,
            content: "",
            library: false,
            projects: [],
            filesize: 0
        };
        setPendingScopeAssignments(prev => [...prev, {
            file: queuedFile,
            assignments: existingAssignments,
            requireContextMeshSelection
        }]);
    }, [fetchFileAssignments]);

    const allowedExtensions = [
        ".pdf", ".doc", ".docx", ".odt", ".rtf",
        ".xls", ".xlsx", ".ods",
        ".ppt", ".pptx", ".odp",
        ".txt", ".html", ".htm", ".css", ".csv", ".tsv",
        ".js", ".mjs", ".ts", ".tsx", ".jsx",
        ".py", ".java", ".c", ".h", ".cs", ".php", ".rb", ".pl",
        ".sh", ".bash",
        ".xml", ".yaml", ".yml", ".md", ".markdown",
        ".sql", ".json", ".graphql", ".gql",
        ".xhtml", ".vue", ".svelte",
        ".ini", ".cfg", ".conf", ".env", ".toml", ".properties",
        ".log", ".rst", ".tex"
    ];

    const isFileAllowed = useCallback((file: File): boolean => {
        const fileName = file.name.toLowerCase();
        return allowedExtensions.some(ext => fileName.endsWith(ext));
    }, []);

    const enqueueFilesForUpload = useCallback((files: File[]) => {
        if (!files.length) return;
        const allowed: File[] = [];
        let sawRejectedType = false;
        files.forEach(file => {
            if (isFileAllowed(file)) allowed.push(file);
            else sawRejectedType = true;
        });
        if (sawRejectedType) {
            setFileTypeError(`File type not supported.\nAllowed types: ${allowedExtensions.join(", ")}`);
        }
        if (allowed.length > 0) {
            setPendingUploadFiles(prev => [...prev, ...allowed]);
        }
    }, [isFileAllowed]);

    // Drag & drop / picker queue → feed one file at a time into the upload modal.
    const onDrop = useCallback((accepted: File[]) => {
        enqueueFilesForUpload(accepted);
    }, [enqueueFilesForUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        accept: {
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.oasis.opendocument.text": [".odt"],
            "application/rtf": [".rtf"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
            "application/vnd.ms-powerpoint": [".ppt"],
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
            "application/vnd.oasis.opendocument.presentation": [".odp"],
            "text/plain": [".txt", ".log", ".ini", ".cfg", ".conf", ".env", ".properties"],
            "text/html": [".html", ".htm", ".xhtml"],
            "text/css": [".css"],
            "text/csv": [".csv", ".tsv"],
            "text/javascript": [".js", ".mjs"],
            "text/typescript": [".ts", ".tsx"],
            "text/jsx": [".jsx"],
            "text/x-python": [".py"],
            "text/x-java": [".java"],
            "text/x-c": [".c", ".h"],
            "text/x-csharp": [".cs"],
            "text/x-php": [".php"],
            "text/x-ruby": [".rb"],
            "text/x-perl": [".pl"],
            "text/x-shellscript": [".sh", ".bash"],
            "text/xml": [".xml"],
            "text/yaml": [".yaml", ".yml"],
            "text/markdown": [".md", ".markdown"],
            "application/sql": [".sql"],
            "application/json": [".json"],
            "application/graphql": [".graphql", ".gql"],
            "text/x-vue": [".vue"],
            "text/x-svelte": [".svelte"],
            "application/toml": [".toml"],
            "text/x-rst": [".rst"],
            "text/x-tex": [".tex"]
        }
    });

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    const handleDropCard = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            enqueueFilesForUpload(Array.from(files));
        }
    }, [enqueueFilesForUpload]);

    const handleBrowseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setDriveLoading(false);
        setDriveError(null);
        setSourceDialogOpen(true);
    }, []);

    const readError = useCallback((error: unknown, fallback: string): string => {
        if (error instanceof Error && error.message) return error.message;
        return fallback;
    }, []);

    const selectFromGoogleDrive = useCallback(async (token: string) => {
        const pickerSession = await getGoogleDrivePickerSession(token);
        if (!pickerSession.accessToken) {
            throw new Error("Google Picker access token is unavailable.");
        }

        const selection = await openGoogleDrivePickerDialog({
            accessToken: pickerSession.accessToken,
            appId: pickerSession.clientId || undefined,
            multiselect: true,
            title: "Select files from Google Drive"
        });
        if (selection.action !== "picked") {
            return;
        }

        const pickedEntries: GoogleDriveEntry[] = selection.docs.map(doc => ({
            ref: doc.id,
            name: doc.name || "Untitled",
            is_folder: false,
            size: doc.sizeBytes
        }));

        setDriveEntries(pickedEntries);
        setDriveSelected(Object.fromEntries(pickedEntries.map(entry => [entry.ref, entry])));
        setDriveSearch("");
        setSourceDialogOpen(false);
        setDriveDialogOpen(true);
    }, []);

    const ensureDriveConnected = useCallback(async (token: string): Promise<"connected" | "redirected"> => {
        const status = await getGoogleDriveStatus(token);
        if (status.connected) return "connected";

        const oauth = await initiateGoogleDriveOAuth({ return_to: window.location.href }, token);
        const authorizationUrl = oauth.authorizationUrl ?? oauth.authUrl;
        if (!authorizationUrl) {
            throw new Error("Google Drive authorization URL is unavailable.");
        }

        const preferSameTab = typeof navigator !== "undefined" && navigator.webdriver;
        if (preferSameTab) {
            window.location.assign(authorizationUrl);
            return "redirected";
        }

        const popup = openOAuthPopup(authorizationUrl, "Connect Google Drive");
        if (!popup) {
            window.location.assign(authorizationUrl);
            return "redirected";
        }

        await waitForOAuthPopup(popup, {
            authorizationUrl,
            completionCheck: async () => {
                const statusCheck = await getGoogleDriveStatus(token);
                return Boolean(statusCheck.connected);
            },
            messageTypes: [
                "google_drive_oauth_success",
                "google_drive_oauth_error",
                "google_drive_oauth_retry",
                "google_drive_oauth_cancel"
            ]
        });

        try {
            popup.close();
        } catch {
            // noop - the provider may sever opener controls during redirects.
        }

        const verified = await getGoogleDriveStatus(token);
        if (!verified.connected) {
            throw new Error("Google Drive authorization was not completed.");
        }
        return "connected";
    }, []);

    const openLocalPicker = useCallback(() => {
        setDriveLoading(false);
        setSourceDialogOpen(false);
        manualInputRef.current?.click();
    }, []);

    const openGoogleDrivePicker = useCallback(async () => {
        const token = await getToken();
        if (!token) {
            setDriveError("Authentication is required to connect Google Drive.");
            return;
        }

        setSourceDialogOpen(false);
        setDriveLoading(true);
        setDriveError(null);
        try {
            const oauthResult = await ensureDriveConnected(token);
            if (oauthResult === "redirected") return;
            await selectFromGoogleDrive(token);
        } catch (error) {
            setDriveError(readError(error, "Could not connect to Google Drive."));
        } finally {
            setDriveLoading(false);
        }
    }, [ensureDriveConnected, getToken, readError, selectFromGoogleDrive]);

    const toggleDriveSelection = useCallback((entry: GoogleDriveEntry) => {
        if (entry.is_folder) return;
        setDriveSelected(previous => {
            const next = { ...previous };
            if (next[entry.ref]) delete next[entry.ref];
            else next[entry.ref] = entry;
            return next;
        });
    }, []);

    const importSelectedDriveFiles = useCallback(async () => {
        const token = await getToken();
        if (!token) {
            setDriveError("Authentication is required to import from Google Drive.");
            return;
        }
        const refs = Object.values(driveSelected).map(entry => ({
            ref: entry.ref,
            name: entry.name,
            size: entry.size,
            is_folder: entry.is_folder
        }));
        if (refs.length === 0) return;

        setDriveLoading(true);
        setDriveError(null);
        try {
            const result = await importGoogleDriveRefs(refs, token);
            fileViewerRef.current?.refresh();
            setDriveDialogOpen(false);

            const reusableOrImportedMatches = (result.results || [])
                .filter(item => (item.status === "existing" || item.status === "imported") && typeof item.fileid === "number");
            if (reusableOrImportedMatches.length > 0) {
                const first = reusableOrImportedMatches[0];
                await queueAssignModalForFile(first.fileid as number, first.filename || "", true);
                setRefreshWarning(
                    `Opened Assign for ${first.filename || "selected file"}. Select a Context Mesh to complete Google Drive import.`
                );
            }

            if (result.failed > 0) {
                const existingCount = result.existing ?? 0;
                setRefreshWarning(`Google Drive import complete: ${result.imported} imported, ${existingCount} existing, ${result.failed} failed.`);
            } else if ((result.existing ?? 0) > 0 && !result.imported) {
                const existingCount = result.existing ?? 0;
                setRefreshWarning(`Google Drive import reused ${existingCount} existing file${existingCount === 1 ? "" : "s"}.`);
            } else if ((result.existing ?? 0) > 0) {
                const existingCount = result.existing ?? 0;
                setRefreshWarning(`Google Drive import complete: ${result.imported} imported, ${existingCount} existing.`);
            } else if (reusableOrImportedMatches.length === 0) {
                setRefreshWarning(null);
            }
        } catch (error) {
            setDriveError(readError(error, "Could not import selected Google Drive files."));
        } finally {
            setDriveLoading(false);
        }
    }, [driveSelected, getToken, queueAssignModalForFile, readError]);

    const filteredDriveEntries = driveEntries.filter(entry =>
        entry.name.toLowerCase().includes(driveSearch.trim().toLowerCase())
    );

    useEffect(() => {
        let active = true;

        const finalizeSameTabOAuth = async () => {
            const params = new URLSearchParams(window.location.search);
            const oauthResult = params.get(DRIVE_OAUTH_RESULT_PARAM);
            if (!oauthResult) return;

            params.delete(DRIVE_OAUTH_RESULT_PARAM);
            const nextSearch = params.toString();
            const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
            window.history.replaceState(window.history.state, "", nextUrl);

            if (oauthResult !== "success") {
                if (active) {
                    setDriveError("Google Drive authorization was not completed.");
                    setDriveLoading(false);
                }
                return;
            }

            const token = await getToken();
            if (!token) {
                if (active) {
                    setDriveError("Authentication is required to connect Google Drive.");
                    setDriveLoading(false);
                }
                return;
            }

            if (!active) return;
            await selectFromGoogleDrive(token);
        };

        void finalizeSameTabOAuth();

        return () => {
            active = false;
        };
    }, [getToken, selectFromGoogleDrive]);

    // Chat-routed intake: enqueue the next pending file and let the shared
    // queue effect feed it into the upload modal in order.
    useProgrammaticIntakeQueue({
        intakeFiles,
        onIntakeConsumed,
        onConsumeFile: file => enqueueFilesForUpload([file])
    });

    useEffect(() => {
        if (fileOpen || showScopeModal || fileToUpload || pendingUploadFiles.length === 0) return;
        const next = pendingUploadFiles[0];
        setPendingUploadFiles(prev => (prev[0] === next ? prev.slice(1) : prev));
        setFileToUpload(next);
        setFileOpen(true);
    }, [fileOpen, showScopeModal, fileToUpload, pendingUploadFiles]);

    // Process one scope assignment at a time to avoid overwriting the current
    // modal state when multiple uploads complete close together.
    useEffect(() => {
        if (showScopeModal || uploadedFile || pendingScopeAssignments.length === 0) return;
        const next = pendingScopeAssignments[0];
        setPendingScopeAssignments(prev => (prev[0] === next ? prev.slice(1) : prev));
        setUploadedFile(next.file);
        setUploadedFileAssignments(next.assignments);
        setUploadedFileRequiresContextMesh(next.requireContextMeshSelection);
        setShowScopeModal(true);
    }, [showScopeModal, uploadedFile, pendingScopeAssignments]);

    // Notify return-to-origin only once the entire upload+scope pipeline is
    // drained. Emitting per-file can navigate away mid-batch and strand
    // pending files/scopes.
    useEffect(() => {
        const pipelineIdle =
            !fileOpen &&
            !showScopeModal &&
            !fileToUpload &&
            !uploadedFile &&
            pendingUploadFiles.length === 0 &&
            pendingScopeAssignments.length === 0;
        if (!pipelineIdle) return;
        if (completedAssignments === 0) return;
        if (returnNotifiedForBatchRef.current) return;
        returnNotifiedForBatchRef.current = true;
        onAssignmentComplete?.();
        setCompletedAssignments(0);
    }, [
        completedAssignments,
        fileOpen,
        fileToUpload,
        onAssignmentComplete,
        pendingScopeAssignments.length,
        pendingUploadFiles.length,
        showScopeModal,
        uploadedFile
    ]);

    const handleManualInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files ? Array.from(e.target.files) : [];
        enqueueFilesForUpload(picked);
        // allow selecting the same file again later
        e.currentTarget.value = "";
    }, [enqueueFilesForUpload]);

    return (
        <MuiBox sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {!hideHeader && (
                <MuiPaper
                    elevation={0}
                    sx={{
                        height: 64,
                        minHeight: 64,
                        maxHeight: 64,
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider",
                        borderRadius: 0,
                        bgcolor: "#F1F5FE",
                        flexShrink: 0
                    }}
                >
                    {onBackToContextMesh && (
                        <NavigationBackButton
                            label="Back to Context Mesh"
                            onClick={onBackToContextMesh}
                            description="Return to the product Context Mesh"
                            bridgeId="btn.back-to-context-mesh"
                            testId="back-to-context-mesh"
                        />
                    )}
                    <MuiTypography variant="h4" sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.25rem" }}>
                        Files & Attachments
                    </MuiTypography>
                    <MuiTypography variant="h4" sx={{ color: "text.disabled", fontWeight: 300, fontSize: "1.25rem", mx: 2 }}>
                        |
                    </MuiTypography>
                    <MuiTypography variant="body1" sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 400 }}>
                        One flat list. Link each file to authorized Products, standalone Context Meshes, or both.
                    </MuiTypography>
                </MuiPaper>
            )}

            <MuiBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {/* Compact uploader strip (V6) */}
                <MuiBox
                    {...getRootProps()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropCard}
                    onClick={handleBrowseClick}
                    sx={{
                        mx: 3, mt: 2, px: "14px", py: "10px",
                        display: "flex", alignItems: "center", gap: "12px", flexShrink: 0,
                        border: `2px dashed ${isDragOver || isDragActive ? T.primary : "rgba(0,0,0,0.12)"}`,
                        borderRadius: "11px",
                        backgroundColor: isDragOver || isDragActive ? T.spineSoft : "transparent",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        "&:hover": { borderColor: T.primary, backgroundColor: T.spineSoft }
                    }}
                >
                    <UploadCloud size={20} color={T.primary} style={{ flexShrink: 0 }} />
                    <MuiBox sx={{ flex: 1, minWidth: 0 }}>
                        <MuiTypography sx={{ fontSize: 13, fontWeight: 600, color: T.fg1 }}>Drop files here or click to browse</MuiTypography>
                        <MuiTypography sx={{ fontSize: 11.5, color: T.fg4 }}>PDF, documents, spreadsheets, markdown, code · max 50MB</MuiTypography>
                    </MuiBox>

                    {/* Hidden input for dropzone (drag&drop) */}
                    <input {...getInputProps()} />

                    {/* Dedicated hidden input for the button-driven selection — keep the existing accept attribute verbatim */}
                    <input ref={manualInputRef} type="file" multiple accept=".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.txt,.html,.htm,.css,.csv,.tsv,.js,.mjs,.ts,.tsx,.jsx,.py,.java,.c,.h,.cs,.php,.rb,.pl,.sh,.bash,.xml,.yaml,.yml,.md,.markdown,.sql,.json,.graphql,.gql,.xhtml,.vue,.svelte,.ini,.cfg,.conf,.env,.toml,.properties,.log,.rst,.tex" style={{ display: "none" }} onClick={event => event.stopPropagation()} onChange={handleManualInputChange} />

                    <MuiButton
                        variant="outlined"
                        size="small"
                        onClick={handleBrowseClick}
                        sx={{ flexShrink: 0, textTransform: "none", borderColor: "rgba(0,0,0,0.12)", color: T.fg2 }}
                    >
                        Choose Files
                    </MuiButton>
                </MuiBox>

                {/* File listing */}
                <MuiBox sx={{ flex: 1, px: 3, pb: 3, pt: "18px", overflow: "hidden", minHeight: 0 }}>
                    {driveError && (
                        <MuiAlert severity="error" onClose={() => setDriveError(null)} sx={{ mb: 1.5 }}>
                            {driveError}
                        </MuiAlert>
                    )}
                    {refreshWarning && (
                        <MuiAlert severity="warning" onClose={() => setRefreshWarning(null)} sx={{ mb: 1.5 }}>
                            {refreshWarning}
                        </MuiAlert>
                    )}
                    <FileViewer ref={fileViewerRef} />
                </MuiBox>
            </MuiBox>

            {/* Upload Modal */}
            <FileUploadVectorFileModal
                isOpen={fileOpen}
                file={fileToUpload || undefined}
                onClose={() => {
                    setFileOpen(false);
                    setFileToUpload(null);
                }}
                onUploadComplete={async (uploadedFileId, uploadedFileName) => {
                    if (typeof uploadedFileId !== "number") return;
                    // The upload and assignment dialogs are both modal. Close the
                    // upload dialog before doing any asynchronous refresh work so
                    // its focus trap/backdrop cannot intercept the assignment UI.
                    setFileOpen(false);
                    setFileToUpload(null);
                    returnNotifiedForBatchRef.current = false;
                    // Refresh the file list first
                    await fileViewerRef.current?.refresh?.();
                    // Use the completion payload directly instead of a delayed
                    // name-based lookup. This avoids stale-view races when
                    // multiple uploads complete close together.
                    let existingAssignments: FileAssignments;
                    try {
                        existingAssignments = await fetchFileAssignments(uploadedFileId);
                    } catch (error) {
                        console.error("Error loading uploaded file assignments:", error);
                        setRefreshWarning(
                            "The file was uploaded, but its assignments could not be loaded. Use Assign in Files to try again."
                        );
                        return;
                    }
                    const queuedFile: VectorFile = {
                        fileid: uploadedFileId,
                        filename: uploadedFileName || `File #${uploadedFileId}`,
                        userid: 0,
                        mime_type: "",
                        createdate: new Date(0),
                        lastupdated: new Date(0),
                        faq: "",
                        overview: "",
                        total_tokens: 0,
                        search_index_chungs: 0,
                        content: "",
                        library: false,
                        projects: [],
                        filesize: 0
                    };
                    setPendingScopeAssignments(prev => [...prev, {
                        file: queuedFile,
                        assignments: existingAssignments,
                        requireContextMeshSelection: false
                    }]);
                }}
            />

            {/* Scope Assignment Modal */}
            <FileScopeModal
                open={showScopeModal}
                file={uploadedFile}
                currentAssignments={uploadedFileAssignments}
                initialProjectId={defaultProjectId}
                initialContextMeshId={defaultContextMeshId}
                requireContextMeshSelection={uploadedFileRequiresContextMesh}
                onRefreshDelayed={setRefreshWarning}
                onClose={() => {
                    setShowScopeModal(false);
                    setUploadedFile(null);
                    setUploadedFileAssignments(null);
                    setUploadedFileRequiresContextMesh(false);
                    fileViewerRef.current?.refresh?.(); // Refresh to show updated scopes
                }}
                onSave={async (savedScope: SavedScopeData) => {
                    console.log("File scope saved:", savedScope);

                    const token = await getToken();
                    if (token) {
                        // Union of added+removed ids — anything that gained OR lost this file
                        // needs a rebuild check (removed_feature_ids carries cleared legacy
                        // feature scopes now that saves are product-only).
                        const allProjectIds = [
                            ...savedScope.changes.added_project_ids,
                            ...savedScope.changes.removed_project_ids
                        ];
                        const allFeatureIds = [
                            ...savedScope.changes.added_feature_ids,
                            ...savedScope.changes.removed_feature_ids
                        ];

                        if (savedScope.is_global) {
                            console.log("File saved as global scope, triggering global rebuild check");
                            markWorkProductsNeedingRebuild(null, null, null, null, true, token).catch(err => {
                                console.error("Error triggering global rebuild:", err);
                            });
                        }

                        if (allProjectIds.length > 0 || allFeatureIds.length > 0) {
                            console.log("Triggering rebuild for changed projects:", allProjectIds, "and features:", allFeatureIds);
                            markWorkProductsNeedingRebuild(
                                allProjectIds.length > 0 ? allProjectIds : null,
                                allFeatureIds.length > 0 ? allFeatureIds : null,
                                null, // user_story_ids
                                null, // source_warehouseid
                                true, // file_update
                                token
                            ).catch(err => {
                                console.error("Error triggering rebuild:", err);
                            });
                        }
                    }

                    // Reset state
                    setUploadedFileAssignments(null);

                    fileViewerRef.current?.refresh?.(); // Refresh to show updated scopes

                    // Assignment finished — let the host offer a ride back to
                    // wherever the user was before the agent brought them here,
                    // but only after the whole batch drains (effect above).
                    setCompletedAssignments(prev => prev + 1);
                }}
            />

            {/* The local-device choice synchronously opens the browser's native
                file picker. A modal Radix dialog applies pointer-events:none to
                the document until its exit lifecycle finishes, but that lifecycle
                is paused by the native picker and can strand the following MUI
                assignment dialog behind the stale pointer lock. This chooser has
                its own overlay and explicit close behavior, so it does not need
                Radix's document-wide modal lock. */}
            <Dialog modal={false} open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Choose file source</DialogTitle>
                        <DialogDescription>Select where files should come from.</DialogDescription>
                    </DialogHeader>
                    <MuiBox sx={{ display: "grid", gap: 1.25 }}>
                        <MuiButton variant="outlined" onClick={openLocalPicker} data-testid="files-source-local">
                            Local device
                        </MuiButton>
                        <MuiButton variant="outlined" onClick={() => void openGoogleDrivePicker()} disabled={driveLoading} data-testid="files-source-google-drive">
                            Google Drive
                        </MuiButton>
                    </MuiBox>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setDriveLoading(false);
                                setSourceDialogOpen(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={driveDialogOpen} onOpenChange={setDriveDialogOpen}>
                <DialogContent className="sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Import from Google Drive</DialogTitle>
                        <DialogDescription>Files selected via Google Picker. Review and import into ProductStudio.</DialogDescription>
                    </DialogHeader>

                    <MuiTextField
                        size="small"
                        fullWidth
                        placeholder="Filter selected files"
                        value={driveSearch}
                        onChange={event => setDriveSearch(event.target.value)}
                    />

                    <MuiBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, mt: 1, maxHeight: 340, overflowY: "auto" }}>
                        {driveLoading ? (
                            <MuiTypography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>Loading files...</MuiTypography>
                        ) : filteredDriveEntries.length === 0 ? (
                            <MuiTypography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>No files found in this location.</MuiTypography>
                        ) : (
                            filteredDriveEntries.map(entry => (
                                <MuiBox
                                    key={entry.ref}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        borderBottom: "1px solid",
                                        borderBottomColor: "divider"
                                    }}
                                >
                                    <MuiCheckbox
                                        size="small"
                                        disabled={entry.is_folder}
                                        checked={Boolean(driveSelected[entry.ref])}
                                        onChange={() => toggleDriveSelection(entry)}
                                    />
                                    <MuiBox sx={{ flex: 1, minWidth: 0 }}>
                                        <MuiTypography sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {entry.name}
                                        </MuiTypography>
                                        <MuiTypography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                                            {entry.is_folder ? "Folder" : "File"}
                                        </MuiTypography>
                                    </MuiBox>
                                </MuiBox>
                            ))
                        )}
                    </MuiBox>

                    <DialogFooter>
                        <MuiTypography sx={{ fontSize: 12, color: "text.secondary", mr: "auto" }}>
                            {Object.keys(driveSelected).length} selected
                        </MuiTypography>
                        <Button type="button" variant="outline" onClick={() => setDriveDialogOpen(false)}>Cancel</Button>
                        <Button
                            type="button"
                            onClick={() => void importSelectedDriveFiles()}
                            disabled={driveLoading || Object.keys(driveSelected).length === 0}
                            data-testid="files-drive-import-selected"
                        >
                            Import selected
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* File Type Error Dialog */}
            <Dialog open={!!fileTypeError} onOpenChange={(open) => !open && setFileTypeError(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>File Type Not Supported</DialogTitle>
                        <DialogDescription className="whitespace-pre-line">{fileTypeError}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => setFileTypeError(null)}>OK</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </MuiBox>
    );
};

export default UploadCenter;
